import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";

import {
  IoDocumentOutline,
  IoSettingsOutline,
  IoEllipsisHorizontal,
  IoCloseCircleOutline,
} from "react-icons/io5";

import Toaster from "../../components/common/Toaster";
import TableSize from "../../components/common/TableSize";
import Dropdown from "../../components/common/Dropdown";
import Modal from "../../components/common/Modal";

import SwornDeclarationBreadcrumbs from "./components/SwornDeclarationBreadcrumbs";
import SwornDeclarationHeader from "./components/SwornDeclarationHeader";
import SwornDeclarationTable from "./components/SwornDeclarationTable";
import SwornDeclarationForm from "./components/SwornDeclarationForm";
import SwornDeclarationDelete from "./components/SwornDeclarationDelete";
import SwornDeclarationFilter from "./components/SwornDeclarationFilter";

import accountService from "../../services/accountService";

const AccountSwornDeclarations = () => {
  const { id } = useParams();

  const [data, setData] = useState([]);
  const [info, setInfo] = useState(null);
  const [account, setAccount] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  const [tableOptions, setTableOptions] = useState({
    start_date: "",
    end_date: "",
    year: "",
    search_query: "",
    current_page: 1,
    page_size: 10,
    sort_column: { path: "created_on", order: "desc" },
  });

  const actions = (item) => {
    return [
      [
        {
          name: "View Document",
          icon: <IoDocumentOutline className="mr-3 text-green-500" />,
          is_disabled: !item.s3_reference_name,
          action: () =>
            window.open(
              item.s3_reference_name,
              "_blank",
              "noopener, noreferrer"
            ),
        },
      ],
      [
        {
          name: "Edit Info",
          icon: <IoSettingsOutline className="mr-3 text-gray-400" />,
          action: () => handleEdit(item),
        },
      ],
      [
        {
          name: "Delete Sworn Declaration",
          icon: <IoCloseCircleOutline className="mr-3 text-red-500" />,
          action: () => handleDelete(item),
        },
      ],
    ];
  };

  const columns = [
    { path: "year", label: "Year" },
    { path: "deadline", label: "Deadline" },
    { path: "created_on", label: "Date Created" },
    {
      key: "actions",
      content: (data) => (
        <Dropdown items={actions(data)} icon={<IoEllipsisHorizontal />} />
      ),
    },
  ];

  const { page_size: size } = tableOptions;

  const settings = [
    {
      id: 1,
      content: <TableSize size={size} onChange={(e) => handleSizeChange(e)} />,
    },
  ];

  const handleOptionsChange = (options) => setTableOptions(options);

  const handleSizeChange = (event) => {
    const page_size = event.target.value;

    if (page_size !== tableOptions.page_size) {
      const options = { ...tableOptions };
      options.page_size = page_size;
      options.current_page = 1;
      setTableOptions(options);
    }
  };

  const handleSearch = (query) => {
    const options = { ...tableOptions };

    if (options.search_query !== query) {
      options.start_date = "";
      options.end_date = "";
      options.year = "";
      options.search_query = query;
      options.current_page = 1;
      handleOptionsChange(options);
    }
  };

  const handleFilter = (start_date, end_date, year) => {
    const options = { ...tableOptions };
    options.current_page = 1;
    options.start_date = start_date;
    options.end_date = end_date;
    options.year = year;
    handleOptionsChange(options);
    setShowFilter(false);
  };

  const handleEdit = (item) => {
    const result = item ?? null;
    setInfo(result);
    setShowForm(!showForm);
  };

  const handleDelete = (item) => {
    const result = item ?? null;
    setInfo(result);
    setShowDelete(!showDelete);
  };

  const handleSuccess = (item) => {
    const index = data.findIndex((i) => i.id === item.id);

    if (index === -1) {
      setData([item, ...data]);
      return;
    }

    const items = [...data];
    items[index] = item;
    setData(items);
  };

  const handleDeleteSuccess = (item) => {
    const items = data.filter((i) => i.id !== item.id);
    setData(items);
    handleDelete();
  };

  const fetchAccount = useCallback(
    async (mounted) => {
      try {
        const result = await accountService.getAccountInfoAsync(id);
        if (result === null) return window.location("/404");

        if (mounted) {
          setAccount(result);
        }
      } catch (ex) {
        Toaster.Error("Something went wrong.");
      }
    },
    [id]
  );

  useEffect(() => {
    let mounted = true;

    fetchAccount(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchAccount]);

  return (
    <div className="px-6 lg:px-0">
      <SwornDeclarationBreadcrumbs account={account} />
      <div className="card p-8 mt-5">
        <SwornDeclarationHeader
          account={account}
          settings={settings}
          onFilter={setShowFilter}
          onSearch={handleSearch}
          onSuccess={handleSuccess}
        />
        <SwornDeclarationTable
          id={id}
          data={data}
          columns={columns}
          tableOptions={tableOptions}
          setData={setData}
          onChange={handleOptionsChange}
        />
      </div>
      <Modal
        title="Filter"
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        borderTop
      >
        <SwornDeclarationFilter onFilter={handleFilter} />
      </Modal>
      <Modal
        title="Update Sworn Declaration"
        isOpen={showForm}
        isLoading={isLoading}
        onClose={() => handleEdit()}
        borderTop
      >
        <SwornDeclarationForm
          data={info}
          account={account}
          isLoading={isLoading}
          setLoading={setIsLoading}
          setShowForm={setShowForm}
          onSuccess={handleSuccess}
        />
      </Modal>
      <Modal
        isOpen={showDelete}
        isLoading={isLoading}
        onClose={() => handleDelete()}
      >
        <SwornDeclarationDelete
          data={info}
          setLoading={setIsLoading}
          onDelete={handleDeleteSuccess}
          onClose={() => handleDelete()}
        />
      </Modal>
    </div>
  );
};

export default AccountSwornDeclarations;
